import { z } from 'zod';

export const HotelSlugSchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9-]+$/, 'lowercase letters, digits, and hyphens only');

/**
 * A crawlable website URL. `.url()` alone is not enough: a mistyped scheme
 * (`hhtps://…`) is still a syntactically valid URL, so it passes validation,
 * the hotel gets saved with it, and only the intake trigger rejects it later —
 * with a 400 the user never sees. Pin the scheme here so the bad value never
 * enters the system.
 */
export const HttpUrlSchema = z
  .string()
  .url()
  .max(2048)
  .refine((v) => /^https?:\/\//i.test(v), {
    message: 'A címnek http:// vagy https:// előtaggal kell kezdődnie',
  });

export const CreateHotelSchema = z.object({
  name: z.string().min(2).max(160),
  slug: HotelSlugSchema,
  websiteUrl: HttpUrlSchema.optional(),
  contactEmail: z.string().email().max(254).optional(),
  contactName: z.string().min(1).max(160).optional(),
  defaultPostFrequencyPerWeek: z.number().int().min(1).max(14).default(2),
  /** Days before an event the AI should start promoting it (0–30). */
  eventLeadDays: z.number().int().min(0).max(30).default(7),
  brandSalesRatio: z.number().min(0).max(1).default(0.7),
});
export type CreateHotel = z.infer<typeof CreateHotelSchema>;

// Active toggle isn't part of the create body — new hotels are active by
// default — but updates may flip it. coverImageUrl is captured by the
// crawler automatically; updates allow setting it manually too (paste a
// URL or upload a file via /hotels/:id/media and pin the result).
export const UpdateHotelSchema = CreateHotelSchema.partial().extend({
  isActive: z.boolean().optional(),
  coverImageUrl: z.string().url().max(2048).nullable().optional(),
});
export type UpdateHotel = z.infer<typeof UpdateHotelSchema>;

export const HotelSchema = z.object({
  id: z.string().guid(),
  organizationId: z.string().guid(),
  name: z.string(),
  slug: z.string(),
  websiteUrl: z.string().nullable(),
  /** Captured during intake from og:image / twitter:image. */
  coverImageUrl: z.string().nullable().optional(),
  contactEmail: z.string().nullable(),
  contactName: z.string().nullable(),
  defaultPostFrequencyPerWeek: z.number().int(),
  eventLeadDays: z.number().int(),
  brandSalesRatio: z.number(),
  /** Inactive hotels are hidden from plan creation but kept for history. */
  isActive: z.boolean().default(true),
  createdBy: z.string().guid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Hotel = z.infer<typeof HotelSchema>;

export const BrandVoiceSchema = z.object({
  tone: z.enum(['warm', 'elegant', 'playful']).default('warm'),
  emojiDensity: z.enum(['high', 'medium', 'low']).default('medium'),
  signaturePhrases: z.array(z.string().max(120)).max(20).default([]),
  avoidWords: z.array(z.string().max(60)).max(40).default([]),
  ctaStyle: z.string().max(120).default('invitation'),
  /** Form of address for generated copy: informal (tegeződés) or formal (magázódás). */
  formality: z.enum(['informal', 'formal']).default('informal'),
  /** Whether AI copy generation emits hashtags — toggled per platform. */
  hashtagsFb: z.boolean().default(false),
  hashtagsIg: z.boolean().default(true),
});
export type BrandVoice = z.infer<typeof BrandVoiceSchema>;

export const UpdateHotelProfileSchema = z.object({
  brandVoice: BrandVoiceSchema.optional(),
  usp: z.array(z.string().max(280)).max(20).optional(),
  amenities: z.array(z.string().max(40)).max(60).optional(),
  locationKeywords: z.array(z.string().max(40)).max(40).optional(),
  contentRules: z.array(z.string().max(280)).max(20).optional(),
});
export type UpdateHotelProfile = z.infer<typeof UpdateHotelProfileSchema>;

export const HotelProfileSchema = z.object({
  hotelId: z.string().guid(),
  brandVoice: BrandVoiceSchema,
  usp: z.array(z.string()),
  amenities: z.array(z.string()),
  locationKeywords: z.array(z.string()),
  contentRules: z.array(z.string()).default([]),
  crawlStatus: z.enum(['pending', 'running', 'done', 'failed']),
  crawlRunAt: z.string().datetime().nullable(),
  crawlError: z.string().nullable(),
  updatedAt: z.string().datetime(),
});
export type HotelProfile = z.infer<typeof HotelProfileSchema>;

/**
 * What deleting a hotel would take with it.
 *
 * The confirm dialog used to assemble these client-side — one `/plans?hotelId`
 * request plus one `/plans/:id/posts` per plan, with each per-plan failure
 * swallowed into an empty array. A partial failure therefore rendered a
 * confidently WRONG, smaller number in a destructive confirmation: the user
 * could approve a deletion believing it touched less than it did.
 */
export const HotelDeletePreviewSchema = z.object({
  plans: z.number().int().nonnegative(),
  posts: z.number().int().nonnegative(),
});
export type HotelDeletePreview = z.infer<typeof HotelDeletePreviewSchema>;
